import { defineStore } from 'pinia'
import { useTenantStore } from '@/stores/tenant';
import { chatService } from '@/services/chatService';
import type { ChatState, Message, Conversation } from '@/types/Chat';
import { useAuthStore } from './auth';
// Lokal bildirim sesi — CDN yok (mixkit bağımlılığı kaldırıldı, ağ/adblock'a takılmaz).
import notificationSoundUrl from '@/assets/notification.wav';
import { isHtml, truncate } from '@/utils/stringUtils';

// WebSocket message types (matching backend)
interface WebSocketMessage {
    type: 'SUBSCRIBE' | 'UNSUBSCRIBE' | 'SEND' | 'TYPING' | 'MARK_READ' |
    'MESSAGE' | 'TYPING_EVENT' | 'READ_EVENT' | 'SUBSCRIBED' | 'UNSUBSCRIBED' |
    'NOTIFICATION' | 'INBOX_NOTIFICATION' | 'ERROR' | 'CONNECTED' | 'PRESENCE_EVENT' | 'MESSAGE_UPDATED';
    conversationId?: string;
    content?: string;
    data?: any;
    error?: string;
    parentMessageId?: string;
    attachmentIds?: string[];
    /** SEND: istemci idempotency anahtarı (localId) — sunucu echo'da geri döndürür. */
    clientMessageId?: string;
}

// WebSocket message types (matching backend)
let typingTimer: any = null;
let lastTypingSentAt = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
// Exponential backoff sayacı — CONNECTED gelince sıfırlanır.
let reconnectAttempts = 0;
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30_000;
const RECONNECT_JITTER_MS = 500;
// SUBSCRIBE gönderildi ama SUBSCRIBED ack'i henüz gelmedi — ack penceresinde
// ikinci bir SUBSCRIBE gönderilmesin (idempotent subscribe).
const subscribeInFlight = new Set<string>();
// Optimistic send: localId -> echo bekleme zamanlayıcısı.
const pendingTimers = new Map<string, ReturnType<typeof setTimeout>>();
// Optimistic send: localId -> attachmentIds (retry'da yeniden gönderilir).
const pendingAttachmentIds = new Map<string, string[] | undefined>();
const PENDING_ECHO_TIMEOUT_MS = 10_000;

export const useChatStore = defineStore('chat', {
    state: (): ChatState => ({
        conversations: [],
        activeConversationId: null,
        messages: [],
        isConnected: false,
        loading: false,
        stompClient: null,
        typingUsers: {},
        userPresence: {},
        subscribedConversations: {} as Record<string, boolean>,
        soundEnabled: true,
        replyingToMessage: null as Message | null,
        editingMessage: null as Message | null,
        pagination: {
            currentPage: 0,
            hasMore: true,
            loadingMore: false,
            oldestSeq: null,
            hasMoreNewer: false,
            loadingNewer: false
        },
        unreadAnchor: null,
        quickReplyManageOpen: false
    }),

    getters: {
        totalUnreadCount: (state) => {
            return state.conversations.reduce((acc, conv) => acc + (conv.unreadCount || 0), 0);
        },
        activeConversation: (state) => {
            return state.conversations.find(c => c.id === state.activeConversationId);
        }
    },

    actions: {
        async fetchConversations() {
            this.loading = true;
            try {
                this.conversations = await chatService.getConversations();
                this.updateDocumentTitle();

                if (this.isConnected && this.stompClient) {
                    this.conversations.forEach(c => this.subscribeToConversation(c.id));
                }
            } finally {
                this.loading = false;
            }
        },

        async fetchHistory(conversationId: string) {
            this.loading = true;
            try {
                this.activeConversationId = conversationId;
                this.pagination.currentPage = 0;
                this.pagination.hasMore = true;
                this.pagination.oldestSeq = null;
                this.pagination.hasMoreNewer = false;
                this.pagination.loadingNewer = false;
                this.unreadAnchor = null;

                // Okunmamış çapası: imleç biliniyorsa sohbet EN ALTTAN değil, ilk
                // okunmamış mesajın etrafından açılır ("N yeni mesaj" ayracı oraya çizilir).
                const conv = this.conversations.find(c => c.id === conversationId);
                const anchorSeq = conv && (conv.unreadCount || 0) > 0 && conv.myLastReadSeq != null
                    ? conv.myLastReadSeq : null;

                if (anchorSeq !== null) {
                    const [afterPage, beforePage] = await Promise.all([
                        chatService.getHistoryAfter(conversationId, anchorSeq),
                        // Ayracın üstünde bağlam: son okunanlardan 15 mesaj.
                        chatService.getHistoryBefore(conversationId, anchorSeq + 1, 15)
                    ]);
                    const older = beforePage.content.slice().reverse();
                    const newer = afterPage.content.slice().reverse();
                    this.messages = [...older, ...newer];
                    this.pagination.hasMore = !beforePage.last;
                    this.pagination.hasMoreNewer = !afterPage.last;
                    this.pagination.oldestSeq = this.oldestLoadedSeq();
                    this.unreadAnchor = { seq: anchorSeq, count: conv!.unreadCount };
                } else {
                    const history = await chatService.getHistory(conversationId, 0);
                    this.messages = history.content.reverse();
                    this.pagination.currentPage = history.number;
                    this.pagination.hasMore = !history.last;
                    this.pagination.oldestSeq = this.oldestLoadedSeq();
                }

                this.markAsRead(conversationId);
            } finally {
                this.loading = false;
            }
        },

        /**
         * Çapa etrafından açılan sohbette AŞAĞI kaydırınca daha yeni mesajları yükler
         * (keyset: yüklü en yeni seq'ten ileri). Kuyruğa yetişince hasMoreNewer=false.
         */
        async fetchNewerHistory() {
            if (!this.activeConversationId || this.pagination.loadingNewer || !this.pagination.hasMoreNewer) return;
            this.pagination.loadingNewer = true;
            try {
                const newestSeq = this.newestLoadedSeq();
                if (newestSeq === null) { this.pagination.hasMoreNewer = false; return; }
                const page = await chatService.getHistoryAfter(this.activeConversationId, newestSeq);
                const known = new Set(this.messages.map(m => m.id));
                page.content.slice().reverse().forEach(m => {
                    if (!known.has(m.id)) this.messages.push(m);
                });
                this.pagination.hasMoreNewer = !page.last;
            } finally {
                this.pagination.loadingNewer = false;
            }
        },

        /** "En yeni"ye atla: kuyruk yüklü değilse yetişene kadar yükler (güvenlik tavanlı). */
        async jumpToLatest() {
            let guard = 0;
            while (this.pagination.hasMoreNewer && guard++ < 6) {
                await this.fetchNewerHistory();
            }
        },

        async fetchMoreHistory() {
            if (!this.activeConversationId || this.pagination.loadingMore || !this.pagination.hasMore) return;

            this.pagination.loadingMore = true;
            try {
                // Keyset: yüklü en eski seq'ten geriye — sayfa kayması yok (yeni mesaj
                // gelirken sayfa-tabanlı çekimde aynı mesaj iki kez inebiliyordu).
                const beforeSeq = this.pagination.oldestSeq;
                const history = beforeSeq !== null
                    ? await chatService.getHistoryBefore(this.activeConversationId, beforeSeq)
                    : await chatService.getHistory(this.activeConversationId, this.pagination.currentPage + 1);

                if (history.content.length > 0) {
                    const olderMessages = history.content.slice().reverse();
                    this.messages = [...olderMessages, ...this.messages];
                }
                this.pagination.currentPage = history.number;
                this.pagination.hasMore = !history.last;
                this.pagination.oldestSeq = this.oldestLoadedSeq();
            } catch (error) {
                // Error handled locally if needed, but no console log
            } finally {
                this.pagination.loadingMore = false;
            }
        },

        /** Yüklü mesajlardaki en küçük seq — keyset "daha eski" cursor'ı. */
        oldestLoadedSeq(): number | null {
            let min: number | null = null;
            for (const m of this.messages) {
                if (typeof m.seq === 'number' && (min === null || m.seq < min)) min = m.seq;
            }
            return min;
        },

        /** Yüklü mesajlardaki en büyük seq — gap-fill "sonrası" cursor'ı. */
        newestLoadedSeq(): number | null {
            let max: number | null = null;
            for (const m of this.messages) {
                if (typeof m.seq === 'number' && (max === null || m.seq > max)) max = m.seq;
            }
            return max;
        },

        initializeWebSocket() {
            const existingSocket = this.stompClient as unknown as WebSocket | null;
            if (existingSocket) {
                if (
                    existingSocket.readyState === WebSocket.OPEN ||
                    existingSocket.readyState === WebSocket.CONNECTING ||
                    existingSocket.readyState === WebSocket.CLOSING
                ) {
                    return;
                }

                this.stompClient = null;
            }

            const authStore = useAuthStore();
            if (!authStore.isAuthenticated) return;

            if (reconnectTimer) {
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            }

            // Base URL from env or dynamic
            let wsUrl = import.meta.env.VITE_WS_URL;

            if (!wsUrl) {
                const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
                wsUrl = `${wsProtocol}//${window.location.host}/ws`;
            }

            // Connecting to WebSocket
            const ws = new WebSocket(wsUrl);
            // Bu soket hiç CONNECTED görmeden kapanırsa handshake reddedilmiş demektir
            // (çoğunlukla süresi dolmuş access token) → reconnect'ten önce auth refresh.
            let handshakeCompleted = false;

            ws.onopen = () => {
                // WebSocket opened
            };

            ws.onmessage = async (event) => {
                try {
                    const message: WebSocketMessage = JSON.parse(event.data);
                    if (message.type === 'CONNECTED') {
                        handshakeCompleted = true;
                    }
                    await this.handleWebSocketMessage(message);
                } catch (e) {
                    // Fail silently or handle error without console log
                }
            };

            ws.onclose = (event) => {
                console.warn('[chat] WebSocket kapandı', { code: event.code, reason: event.reason });
                this.isConnected = false;
                this.subscribedConversations = {};
                subscribeInFlight.clear();
                if (this.stompClient === ws) {
                    this.stompClient = null;
                }

                // Reconnect (unless intentional close)
                if (event.code !== 1000 && authStore.isAuthenticated) {
                    // 1008 (policy violation / auth) veya handshake reddi:
                    // önce oturumu tazele, sonra yeniden bağlan.
                    const needsAuthRefresh = event.code === 1008 || !handshakeCompleted;
                    this.scheduleReconnect(needsAuthRefresh);
                }
            };

            ws.onerror = (event) => {
                console.warn('[chat] WebSocket hatası', event);
            };

            this.stompClient = ws as any;
        },

        /**
         * Exponential backoff + jitter ile yeniden bağlanma planla (üst sınır 30 sn).
         * refreshAuthFirst: token süresi dolmuş olabilir — bağlanmadan önce
         * BaseApiClient'ın refresh akışı (refreshSession) çağrılır.
         */
        scheduleReconnect(refreshAuthFirst = false) {
            if (reconnectTimer) return;

            const backoff = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** reconnectAttempts);
            const delay = backoff + Math.floor(Math.random() * RECONNECT_JITTER_MS);
            reconnectAttempts++;

            reconnectTimer = setTimeout(async () => {
                reconnectTimer = null;
                if (refreshAuthFirst) {
                    try {
                        await chatService.refreshSession();
                    } catch {
                        // Refresh de düştüyse yine de bağlanmayı dene;
                        // kalıcı auth hatasında BaseApiClient logout'u yönetir.
                    }
                }
                this.initializeWebSocket();
            }, delay);
        },

        async handleWebSocketMessage(message: WebSocketMessage) {

            switch (message.type) {
                case 'CONNECTED':
                    this.isConnected = true;
                    reconnectAttempts = 0;

                    // Initialize presence from data (list of online user IDs)
                    if (Array.isArray(message.data)) {
                        const presenceMap: Record<string, boolean> = {};
                        message.data.forEach((userId: string) => {
                            presenceMap[userId] = true;
                        });
                        this.userPresence = presenceMap;
                    }

                    // Subscribe to existing conversations
                    this.conversations.forEach(conv => {
                        this.subscribeToConversation(conv.id);
                    });

                    // Gap-fill: kopukluk sırasında kaçan konuşma/mesajları REST'ten tazele
                    await this.gapFill();
                    break;

                case 'PRESENCE_EVENT':
                    if (message.data && message.data.userId) {
                        const { userId, isOnline } = message.data;
                        this.userPresence[userId] = isOnline;

                        // Also notify global presence store
                        const { usePresenceStore } = await import('./presence');
                        const presenceStore = usePresenceStore();
                        presenceStore.handlePresenceEvent(userId, isOnline);
                    }
                    break;

                case 'SUBSCRIBED':
                    if (message.conversationId) {
                        subscribeInFlight.delete(message.conversationId);
                        this.subscribedConversations[message.conversationId] = true;
                    }
                    break;

                case 'MESSAGE':
                    this.handleIncomingMessage(message.data);
                    break;

                case 'MESSAGE_UPDATED':
                    this.applyMessageUpdate(message.data);
                    break;

                case 'TYPING_EVENT':
                    this.handleTypingEvent(message.data, message.conversationId);
                    break;

                case 'READ_EVENT':
                    this.handleReadEvent(message.data, message.conversationId);
                    break;

                case 'NOTIFICATION':
                    this.handleNotification(message.data);
                    break;

                case 'INBOX_NOTIFICATION': {
                    // Sistem bildirimi anlık olarak zile düşer (fetch beklemeden)
                    // + sağ altta toast: kullanıcı zile bakmıyorsa da haberdar olur.
                    const { useNotificationStore } = await import('./notification');
                    useNotificationStore().addNotification(message.data);
                    const { useToast: useInboxToast } = await import('@/composables/useToast');
                    const { default: i18n } = await import('@/i18n');
                    // Bildirim içeriği çoğu kodda TAM HTML e-posta gövdesidir (9 KB+); toast
                    // onu düz metin bastığı için ekran ham kaynak koduyla doluyordu
                    // (16 Eyl 2026). HTML gövde toast'a GİRMEZ — başlık zaten açıklayıcı
                    // ("Yeni Talep: …"). Yalnız kısa düz metin içerik başlığın altında çıkar.
                    const noteTitle: string | undefined = message.data?.title;
                    const rawContent: string = typeof message.data?.content === 'string'
                        ? message.data.content
                        : '';
                    const plainContent = isHtml(rawContent) ? '' : truncate(rawContent, 160);
                    useInboxToast().info(
                        plainContent || noteTitle || i18n.global.t('notification.toast.newInbox'),
                        plainContent ? noteTitle : undefined,
                        6000
                    );
                    break;
                }

                case 'ERROR':
                    const { useToast } = await import('@/composables/useToast');
                    const toast = useToast();
                    toast.error(message.error || 'A chat error occurred');
                    break;
            }
        },

        async handleIncomingMessage(data: Message) {
            if (!data) return;

            const authStore = useAuthStore();
            const newMessage: Message = data;
            const isOwn = newMessage.senderId === authStore.user?.id;
            const isActive = this.activeConversationId === newMessage.conversationId;
            const isHidden = typeof document !== 'undefined' && document.hidden;
            const hasFocus = typeof document !== 'undefined' && !document.hidden && document.hasFocus();

            if (isActive) {
                // Echo eşleştirme: kendi optimistic balonum sunucu mesajıyla değiştirilir —
                // clientMessageId ile birebir; anahtarsız (eski) echo'da senderId+içerik sezgisi.
                const matchedPending = isOwn && this.resolvePendingEcho(newMessage);

                // Dedup: reconnect/çift SUBSCRIBE sonrası aynı mesaj iki kez gelebilir.
                // hasMoreNewer iken kuyruk yüklü değil — canlı mesajı push'lamak arada
                // boşluk bırakır; mesaj fetchNewerHistory yetişince (id-dedup ile) gelir.
                if (!matchedPending && !this.pagination.hasMoreNewer
                    && !this.messages.some(m => m.id === newMessage.id)) {
                    this.messages.push(newMessage);
                }

                // Otomatik MARK_READ yalnız sekme görünür VE pencere odaklıyken —
                // odak dönünce onWindowFocused() görünürdeki mesajları işaretler.
                if (!isOwn && hasFocus) {
                    this.markAsRead(newMessage.conversationId);
                }
            } else if (!isOwn && !isHidden) {
                // Aktif olmayan sohbet + görünür sekme → toast.
                // Sekme gizliyken toast yerine ses + document.title rozeti (aşağıda).
                const { useToast } = await import('@/composables/useToast');
                const toast = useToast();
                // Uzun mesaj toast'ı ekran boyu büyütmesin — balonun tamamı sohbette zaten var.
                toast.info(truncate(newMessage.content, 160), `New message: ${newMessage.senderName}`);
            }

            const conversation = this.conversations.find(c => c.id === newMessage.conversationId);
            if (conversation) {
                conversation.lastMessagePreview = newMessage.content;
                conversation.lastMessageDeleted = false;
                conversation.lastMessageAt = newMessage.sentAt;

                // Kendi mesajım (başka sekme/cihazdan) unread artırmaz.
                // Aktif sohbet ama odak yoksa da unread artar — odak dönünce sıfırlanır.
                if (!isOwn && (!isActive || !hasFocus)) {
                    conversation.unreadCount = (conversation.unreadCount || 0) + 1;
                }

                // Yeni mesaj gelen sohbet listede öne geçsin.
                this.sortConversations();
                this.updateDocumentTitle();
            }

            // Ses: mesaj geldi VE (sekme gizli VEYA pencere odaksız VEYA aktif sohbet değil).
            if (!isOwn && (isHidden || !hasFocus || !isActive)) {
                this.playNotificationSound();
            }
        },

        /**
         * Kendi mesajımın sunucu echo'sunu pending/failed optimistic balonla eşleştirir.
         * Echo'da clientMessageId varsa YALNIZ onunla eşleşir (birebir; yanlış balonu
         * tüketme riski yok) — anahtarsız echo'da senderId+içerik sezgisine düşer.
         * Eşleşme varsa balon yerine sunucu mesajı konur ve true döner.
         */
        resolvePendingEcho(serverMessage: Message): boolean {
            const idx = serverMessage.clientMessageId
                ? this.messages.findIndex(m =>
                    (m.pending || m.failed) && m.localId === serverMessage.clientMessageId)
                : this.messages.findIndex(m =>
                    (m.pending || m.failed) &&
                    m.senderId === serverMessage.senderId &&
                    m.content === serverMessage.content);
            if (idx === -1) return false;

            const target = this.messages[idx];
            if (!target) return false;
            const localId = target.localId;
            if (localId) {
                const timer = pendingTimers.get(localId);
                if (timer) clearTimeout(timer);
                pendingTimers.delete(localId);
                pendingAttachmentIds.delete(localId);
            }
            this.messages.splice(idx, 1, serverMessage);
            return true;
        },

        /** Konuşma listesini son mesaj zamanına göre (yeni → eski) sıralar. */
        sortConversations() {
            this.conversations.sort((a, b) => {
                const ta = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
                const tb = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
                return tb - ta;
            });
        },

        /**
         * Reconnect sonrası gap-fill: kopukluk sırasında kaçanlar REST'ten çekilir,
         * id-bazlı merge edilir (mevcut state ezilmez, mükerrer mesaj eklenmez).
         */
        async gapFill() {
            try {
                const fresh = await chatService.getConversations();
                const byId = new Map(this.conversations.map(c => [c.id, c] as const));
                fresh.forEach(fc => {
                    const existing = byId.get(fc.id);
                    if (existing) {
                        Object.assign(existing, fc);
                    } else {
                        this.conversations.push(fc);
                    }
                });
                this.sortConversations();
                this.updateDocumentTitle();

                // Kopukluk sırasında eklendiğimiz yeni konuşmalara da abone ol.
                this.conversations.forEach(c => this.subscribeToConversation(c.id));

                if (this.activeConversationId) {
                    // Cursor biliniyorsa yalnız kaçanlar iner (afterSeq); bilinmiyorsa son sayfa.
                    const newestSeq = this.newestLoadedSeq();
                    const history = newestSeq !== null
                        ? await chatService.getHistoryAfter(this.activeConversationId, newestSeq)
                        : await chatService.getHistory(this.activeConversationId, 0);
                    const known = new Set(this.messages.map(m => m.id));
                    const incoming = history.content.slice().reverse();
                    let changed = false;
                    incoming.forEach(m => {
                        if (known.has(m.id)) return;
                        // Kaybolan echo: pending balonla eşleşiyorsa balonu değiştir.
                        if (!this.resolvePendingEcho(m)) {
                            this.messages.push(m);
                            changed = true;
                        }
                    });
                    if (changed) {
                        // Pending balonların sentAt'i lokal saat — kronolojik sırayı koru.
                        this.messages.sort((a, b) =>
                            new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime()
                        );
                    }
                }
            } catch {
                // REST tazeleme başarısız — WS akışı çalışmaya devam eder.
            }
        },

        /**
         * Pencere odağı geri geldi: aktif sohbette görünür duran okunmamışlar işaretlenir.
         * (Aktif sohbete odaksızken gelen mesajlar MARK_READ göndermez — burada gönderilir.)
         */
        onWindowFocused() {
            if (!this.activeConversationId || !this.isConnected) return;
            const conv = this.conversations.find(c => c.id === this.activeConversationId);
            if (conv && (conv.unreadCount || 0) > 0) {
                this.markAsRead(this.activeConversationId);
            }
        },

        handleTypingEvent(data: any, conversationId?: string) {
            if (!data || !conversationId) return;

            const authStore = useAuthStore();
            if (data.userId === authStore.user?.id) return;

            const userName = data.userName || 'Someone';

            if (!this.typingUsers[conversationId]) {
                this.typingUsers[conversationId] = [];
            }

            if (data.isTyping) {
                if (!this.typingUsers[conversationId].includes(userName)) {
                    this.typingUsers[conversationId].push(userName);
                }

                // Auto-remove after 5 seconds
                setTimeout(() => {
                    if (this.typingUsers[conversationId]) {
                        this.typingUsers[conversationId] = this.typingUsers[conversationId].filter(
                            (name: string) => name !== userName
                        );
                    }
                }, 5000);
            } else {
                this.typingUsers[conversationId] = this.typingUsers[conversationId].filter(
                    (name: string) => name !== userName
                );
            }
        },

        handleReadEvent(data: any, conversationId?: string) {
            if (!data || !conversationId) return;

            const { userId, readAt } = data;
            const conv = this.conversations.find(c => c.id === conversationId);

            // READ_EVENT kendi kullanıcım içinse (başka sekme/cihaz okudu):
            // bu sekmedeki unread sayacı da senkron düşsün.
            const authStore = useAuthStore();
            if (userId === authStore.user?.id) {
                if (conv) {
                    conv.unreadCount = 0;
                }
                this.updateDocumentTitle();
            }
            const participant = conv?.participants?.find((p: any) => p.id === userId);
            const userName = participant ? (participant.firstname + ' ' + participant.lastname) : 'Someone';

            // Update all messages in this conversation sent before readAt
            // that don't already have this user in readBy
            this.messages = this.messages.map(msg => {
                if (msg.conversationId === conversationId &&
                    new Date(msg.sentAt) <= new Date(readAt) &&
                    msg.senderId !== userId) {

                    const alreadyRead = msg.readBy?.some(r => r.name.includes(userName));
                    if (!alreadyRead) {
                        return {
                            ...msg,
                            isRead: true,
                            readBy: [...(msg.readBy || []), { name: userName, readAt }]
                        } as Message;
                    }
                }
                return msg;
            });
        },

        async handleNotification(data: any) {
            if (!data) return;

            // Check if it's a general notification (has content/status) or a conversation (has participants)
            if (data.participants) {
                // New conversation notification
                const exists = this.conversations.find(c => c.id === data.id);
                if (!exists && data.id) {
                    this.conversations.unshift(data);
                    this.subscribeToConversation(data.id);

                    const { useToast } = await import('@/composables/useToast');
                    const toast = useToast();
                    toast.success(`You were added to a new conversation: ${data.title}`);
                    this.playNotificationSound();
                }
            } else if (data.content && data.id) {
                // It's a general system notification
                const { useNotificationStore } = await import('./notification');
                const notificationStore = useNotificationStore();
                notificationStore.addNotification(data);

                // Also play sound for system notifications
                this.playNotificationSound();
            }
        },

        subscribeToConversation(conversationId: string) {
            if (!conversationId) return;
            // Idempotent: abone olunmuşsa VEYA ack penceresindeyse ikinci SUBSCRIBE gitmez.
            if (this.subscribedConversations[conversationId] || subscribeInFlight.has(conversationId)) {
                return;
            }
            if (!this.stompClient || !this.isConnected) {
                return;
            }

            const ws = this.stompClient as unknown as WebSocket;
            if (ws.readyState !== WebSocket.OPEN) {
                return;
            }

            const message: WebSocketMessage = {
                type: 'SUBSCRIBE',
                conversationId
            };

            subscribeInFlight.add(conversationId);
            ws.send(JSON.stringify(message));
        },

        playNotificationSound() {
            if (!this.soundEnabled) return;
            const audio = new Audio(notificationSoundUrl);
            // 0.4 çok kısıktı — "Çift Ton" sesiyle birlikte 0.8'e çekildi (11 Ağu seçimi).
            audio.volume = 0.8;
            audio.play().catch(() => { /* Sound play blocked by browser */ });
        },

        updateDocumentTitle() {
            const count = this.totalUnreadCount;
            const baseTitle = useTenantStore().appName;
            if (count > 0) {
                document.title = `(${count}) ${baseTitle}`;
            } else {
                document.title = baseTitle;
            }
        },

        /**
         * Optimistic send: mesaj anında pending balon olarak listeye girer,
         * sunucu echo'su gelince gerçek mesajla değiştirilir (resolvePendingEcho).
         * Bağlantı yoksa false döner — çağıran input'u TEMİZLEMEMELİ ve
         * "gönderilemedi" göstermeli (sessiz kayıp yok).
         */
        sendMessage(content: string, attachmentIds?: string[]): boolean {
            if (!this.activeConversationId) return false;

            const ws = this.stompClient as unknown as WebSocket | null;
            if (!this.isConnected || !ws || ws.readyState !== WebSocket.OPEN) {
                return false;
            }

            const authStore = useAuthStore();
            const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            const optimistic: Message = {
                id: localId,
                localId,
                conversationId: this.activeConversationId,
                senderId: authStore.user?.id || '',
                senderName: [authStore.user?.firstname, authStore.user?.lastname].filter(Boolean).join(' '),
                content,
                type: 'TEXT',
                sentAt: new Date().toISOString(),
                isRead: false,
                pending: true,
                clientMessageId: localId,
                parentMessageId: this.replyingToMessage?.id,
                parentMessageContent: this.replyingToMessage?.content,
                parentMessageSenderName: this.replyingToMessage?.senderName
            };
            this.messages.push(optimistic);
            pendingAttachmentIds.set(localId, attachmentIds);
            this.startPendingTimer(localId);

            const message: WebSocketMessage = {
                type: 'SEND',
                conversationId: this.activeConversationId,
                content,
                attachmentIds,
                // Idempotency: retry aynı anahtarla gider — sunucu mükerrer satır üretmez.
                clientMessageId: localId,
                parentMessageId: this.replyingToMessage?.id
            };

            ws.send(JSON.stringify(message));
            this.replyingToMessage = null; // Clear after sending
            return true;
        },

        /** Echo süresinde gelmezse balonu "gönderilemedi / yeniden dene" durumuna al. */
        startPendingTimer(localId: string) {
            pendingTimers.set(localId, setTimeout(() => {
                pendingTimers.delete(localId);
                const msg = this.messages.find(m => m.localId === localId);
                if (msg && msg.pending) {
                    msg.pending = false;
                    msg.failed = true;
                }
            }, PENDING_ECHO_TIMEOUT_MS));
        },

        /** "Yeniden dene": failed balonu tekrar pending yapıp SEND'i yineler. */
        retryMessage(localId: string) {
            const msg = this.messages.find(m => m.localId === localId);
            if (!msg || !msg.failed) return;

            const ws = this.stompClient as unknown as WebSocket | null;
            if (!this.isConnected || !ws || ws.readyState !== WebSocket.OPEN) return;

            msg.failed = false;
            msg.pending = true;
            msg.sentAt = new Date().toISOString();
            this.startPendingTimer(localId);

            ws.send(JSON.stringify({
                type: 'SEND',
                conversationId: msg.conversationId,
                content: msg.content,
                attachmentIds: pendingAttachmentIds.get(localId),
                // Aynı idempotency anahtarı: ilk SEND aslında ulaştıysa sunucu yeni satır
                // açmaz, var olan mesajı echo'lar — çift mesaj oluşmaz.
                clientMessageId: localId,
                parentMessageId: msg.parentMessageId
            } as WebSocketMessage));
        },

        setReplyMessage(message: Message | null) {
            this.replyingToMessage = message;
            if (message) this.editingMessage = null;
        },

        /** Düzeltme moduna gir/çık (yalnız kendi, gönderilmiş, geri alınmamış mesaj). */
        setEditingMessage(message: Message | null) {
            this.editingMessage = message;
            if (message) this.replyingToMessage = null;
        },

        /**
         * Düzeltilen/geri alınan mesajı yerinde değiştirir (MESSAGE_UPDATED ya da REST yanıtı —
         * ikisi de gelebilir, işlem idempotent). Yanıt alıntıları ve sohbet önizlemesi de güncellenir.
         */
        applyMessageUpdate(data: Message) {
            if (!data?.id) return;
            const idx = this.messages.findIndex(m => m.id === data.id);
            if (idx !== -1) {
                this.messages.splice(idx, 1, { ...this.messages[idx], ...data });
            }
            for (const m of this.messages) {
                if (m.parentMessageId !== data.id) continue;
                m.parentMessageDeleted = !!data.deleted;
                m.parentMessageContent = data.deleted ? undefined : data.content;
            }
            if (this.replyingToMessage?.id === data.id && data.deleted) this.replyingToMessage = null;
            if (this.editingMessage?.id === data.id && data.deleted) this.editingMessage = null;

            const conversation = this.conversations.find(c => c.id === data.conversationId);
            // Önizleme yalnız güncellenen mesaj sohbetin son mesajıysa değişir.
            const sent = this.activeConversationId === data.conversationId
                ? this.messages.filter(m => !m.pending && !m.failed) : [];
            const last = sent[sent.length - 1];
            if (conversation && (last?.id === data.id || conversation.lastMessageAt === data.sentAt)) {
                conversation.lastMessageDeleted = !!data.deleted;
                conversation.lastMessagePreview = data.deleted ? '' : data.content;
            }
        },

        /** Kendi mesajını düzelt; başarıda yerinde güncellenir (WS yayını diğer sekmelere). */
        async editMessage(messageId: string, content: string): Promise<boolean> {
            try {
                this.applyMessageUpdate(await chatService.editMessage(messageId, content));
                this.editingMessage = null;
                return true;
            } catch {
                return false;
            }
        },

        /** Kendi mesajını geri al. */
        async unsendMessage(messageId: string): Promise<boolean> {
            try {
                this.applyMessageUpdate(await chatService.unsendMessage(messageId));
                return true;
            } catch {
                return false;
            }
        },

        async startConversation(participantId: string) {
            try {
                this.loading = true;
                const conversation = await chatService.startConversation(participantId);

                const exists = this.conversations.find(c => c.id === conversation.id);
                if (!exists) {
                    this.conversations.unshift(conversation);
                    this.subscribeToConversation(conversation.id);
                }

                this.activeConversationId = conversation.id;
                await this.fetchHistory(conversation.id);
                this.markAsRead(conversation.id);
            } finally {
                this.loading = false;
            }
        },

        async createGroup(title: string, participantIds: string[]) {
            try {
                this.loading = true;
                const conversation = await chatService.createGroup(title, participantIds);
                this.conversations.unshift(conversation);
                this.subscribeToConversation(conversation.id);
                this.activeConversationId = conversation.id;
                this.messages = [];
            } finally {
                this.loading = false;
            }
        },

        sendTypingStatus(isTyping: boolean) {
            if (!this.isConnected || !this.activeConversationId || !this.stompClient) return;

            const ws = this.stompClient as unknown as WebSocket;
            if (ws.readyState !== WebSocket.OPEN) return;

            const authStore = useAuthStore();

            // Internal helper to send the packet
            const sendPacket = (status: boolean) => {
                const message: WebSocketMessage = {
                    type: 'TYPING',
                    conversationId: this.activeConversationId as string,
                    data: {
                        userId: authStore.user?.id,
                        userName: authStore.user?.firstname,
                        isTyping: status
                    }
                };
                ws.send(JSON.stringify(message));
                lastTypingSentAt = status ? Date.now() : 0;
            };

            if (isTyping) {
                // Throttling: only send 'true' once every 3 seconds
                const now = Date.now();
                if (now - lastTypingSentAt > 3000) {
                    sendPacket(true);
                }

                // Clear existing auto-stop timer
                if (typingTimer) clearTimeout(typingTimer);

                // Set new auto-stop timer
                typingTimer = setTimeout(() => {
                    sendPacket(false);
                    typingTimer = null;
                }, 4000); // Stop typing status after 4 seconds of silence
            } else {
                // Explicit stop
                if (typingTimer) {
                    clearTimeout(typingTimer);
                    typingTimer = null;
                }
                sendPacket(false);
            }
        },

        setActiveConversation(id: string | null) {
            this.activeConversationId = id;
            if (id) {
                this.markAsRead(id);
            }
        },
        // Havuz koltuğu (Takım Gelen Kutusu) açılırken metadata'yı (managerSeat/creatorId/masked)
        // store'a taşı ki ChatWindow konuşmayı bulup doğru hizalasın.
        addConversation(conversation: Conversation) {
            if (!this.conversations.find(c => c.id === conversation.id)) {
                this.conversations.unshift(conversation);
            }
        },
        markAsRead(conversationId: string) {
            if (!this.isConnected || !this.stompClient) return;

            const ws = this.stompClient as unknown as WebSocket;
            if (ws.readyState !== WebSocket.OPEN) return;

            const message: WebSocketMessage = {
                type: 'MARK_READ',
                conversationId
            };

            ws.send(JSON.stringify(message));

            // Update local unread count
            const conversation = this.conversations.find(c => c.id === conversationId);
            if (conversation) {
                conversation.unreadCount = 0;
            }
            this.updateDocumentTitle();
        },

        async deleteConversation(conversationId: string) {
            try {
                await chatService.deleteConversation(conversationId);
                this.conversations = this.conversations.filter(c => c.id !== conversationId);
                if (this.activeConversationId === conversationId) {
                    this.activeConversationId = null;
                    this.messages = [];
                }
            } catch (error) {
                throw error;
            }
        },

        disconnect() {
            if (reconnectTimer) {
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            }
            reconnectAttempts = 0;
            subscribeInFlight.clear();
            pendingTimers.forEach(t => clearTimeout(t));
            pendingTimers.clear();
            pendingAttachmentIds.clear();

            if (this.stompClient) {
                const ws = this.stompClient as unknown as WebSocket;
                this.stompClient = null;
                if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
                    ws.close(1000, 'User logout');
                }
            }
            this.isConnected = false;
            this.subscribedConversations = {};
            this.userPresence = {};
        }
    }
});
